import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = {
    company_name: '', contact_name: '', phone: '', email: '',
    bank_name: '', bank_account: '',
    split_config: { partner_percentage: 65, distributor_percentage: 5, pharmacy_percentage: 30 }
  };
  const [form, setForm] = useState(empty);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const res = await api.get('/partners');
      setPartners(res.data);
    } catch (e) { console.error(e); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setShowModal(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      company_name: p.company_name, contact_name: p.contact_name, phone: p.phone,
      email: p.email || '', bank_name: p.bank_name || '', bank_account: p.bank_account || '',
      split_config: p.split_config || { partner_percentage: 65, distributor_percentage: 5, pharmacy_percentage: 30 }
    });
    setShowModal(true);
  };

  const validateSplit = () => {
    const s = form.split_config;
    const total = (s.partner_percentage || 0) + (s.distributor_percentage || 0) + (s.pharmacy_percentage || 0);
    return Math.abs(total - 100) < 0.01;
  };

  const save = async () => {
    if (!form.company_name || !form.contact_name || !form.phone) {
      toast.error('Company name, contact name and phone are required');
      return;
    }
    if (!validateSplit()) {
      toast.error('Split must total 100%');
      return;
    }
    try {
      if (editing) {
        await api.put(`/partners/${editing}`, form);
        toast.success('Partner updated');
      } else {
        await api.post('/partners', form);
        toast.success('Partner created');
      }
      setShowModal(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const setSplit = (key, val) => setForm({ ...form, split_config: { ...form.split_config, [key]: parseFloat(val) || 0 } });

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>Partners</h1>
        <Button data-testid="new-partner-btn" onClick={openNew} style={{ backgroundColor: '#1E3A8A' }}>
          <Plus size={16} className="mr-2" /> Add Partner
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Bank</th>
              <th className="text-center p-3">Split (P / D / Ph)</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners.map(p => {
              const s = p.split_config || {};
              return (
                <tr key={p.id} data-testid={`partner-row-${p.id}`} className="border-t border-slate-200">
                  <td className="p-3 font-medium">{p.company_name}</td>
                  <td className="p-3">{p.contact_name}</td>
                  <td className="p-3">{p.phone}</td>
                  <td className="p-3 text-slate-600">{p.email || '-'}</td>
                  <td className="p-3 text-xs">{p.bank_name}<br/>{p.bank_account}</td>
                  <td className="p-3 text-center font-mono text-xs">{s.partner_percentage}% / {s.distributor_percentage}% / {s.pharmacy_percentage}%</td>
                  <td className="p-3"><span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">{p.status?.toUpperCase()}</span></td>
                  <td className="p-3 text-right">
                    <Button data-testid={`edit-partner-${p.id}`} onClick={() => openEdit(p)} size="sm" variant="outline">
                      <PencilSimple size={14} className="mr-1" /> Edit
                    </Button>
                  </td>
                </tr>
              );
            })}
            {partners.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-slate-500">No partners yet</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Edit Partner' : 'New Partner'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Company Name *</Label><Input data-testid="partner-company" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} /></div>
              <div><Label>Contact Name *</Label><Input data-testid="partner-contact" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
              <div><Label>Phone *</Label><Input data-testid="partner-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
              <div><Label>Email</Label><Input data-testid="partner-email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} /></div>
              <div><Label>Bank Account</Label><Input value={form.bank_account} onChange={e => setForm({...form, bank_account: e.target.value})} /></div>
            </div>
            <div>
              <Label className="mb-2 block">Split Configuration (must total 100%)</Label>
              <div className="grid grid-cols-3 gap-4">
                <div><Label className="text-xs">Partner %</Label><Input type="number" step="0.1" value={form.split_config.partner_percentage} onChange={e => setSplit('partner_percentage', e.target.value)} /></div>
                <div><Label className="text-xs">Distributor %</Label><Input type="number" step="0.1" value={form.split_config.distributor_percentage} onChange={e => setSplit('distributor_percentage', e.target.value)} /></div>
                <div><Label className="text-xs">Pharmacy %</Label><Input type="number" step="0.1" value={form.split_config.pharmacy_percentage} onChange={e => setSplit('pharmacy_percentage', e.target.value)} /></div>
              </div>
              <p className="text-xs mt-2 text-slate-500">Total: {(form.split_config.partner_percentage || 0) + (form.split_config.distributor_percentage || 0) + (form.split_config.pharmacy_percentage || 0)}%</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button data-testid="save-partner-btn" onClick={save} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
